import { useState, useCallback } from "react";
import { apiHandler } from "../../api/index";
import { endpoint } from "../../api/endpoint";
import { useSelector } from "react-redux";

export const useModelUpload = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const token = useSelector((state) => state.auth.token);

  const uploadModel = useCallback(
    async (formData) => {
      setIsLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await apiHandler({
          url: endpoint.UPLOAD_MODEL,
          method: "POST",
          data: formData,
          authToken: { token },
          headers: {
            // Let axios handle Content-Type for FormData
            "Content-Type": undefined,
          },
        });

        if (response.status === 200 || response.status === 201) {
          setSuccess(true);
          setIsLoading(false);
          return { success: true, data: response.data };
        } else {
          // Extract error message from various possible response structures
          const errorMessage =
            response.data?.error_message ||
            response.data?.message ||
            response.data?.error ||
            `Server error: ${response.status}`;
          
          console.error("Upload failed with status:", response.status, "Response:", response.data);
          setError(errorMessage);
          setIsLoading(false);
          return { success: false, error: errorMessage };
        }
      } catch (err) {
        const errorMessage = err.message || "An error occurred during upload";
        console.error("Upload exception:", err);
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [token]
  );

  const updateModel = useCallback(
    async (modelId, modelData) => {
      setIsLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await apiHandler({
          url: `${endpoint.UPDATE_MODEL || "/api/models"}/${modelId}`,
          method: "PUT",
          data: modelData,
          authToken: { token },
          headers: {
            // Let axios set the multipart boundary for FormData
            "Content-Type": undefined,
          },
        });

        if (response.status >= 200 && response.status < 300) {
          setSuccess(true);
          setIsLoading(false);
          return { success: true, data: response.data };
        }

        const errorMessage =
          response.data?.error_message ||
          response.data?.message ||
          response.data?.error ||
          `Server error: ${response.status}`;

        console.error("Update failed with status:", response.status, "Response:", response.data);
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      } catch (err) {
        const errorMessage = err.message || "An error occurred during model update";
        console.error("Update exception:", err);
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [token]
  );

  const resetState = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    uploadModel,
    updateModel,
    isLoading,
    error,
    success,
    resetState,
  };
};
